import React from "react";
import { RightPanel } from "../../../../../../components/RightPanel";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { CustomLink } from "../../../../../../components/CustomLink/CustomLink";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { LoadState } from "../../../../../../constants/enums";
import { endPoints } from "../../../../../../constants/endPoints";
import { api } from "../../../../../../api/api";
import { IQuotesRequest } from "../../../../../../redux/Quote/quote.types";
import { formatText } from "../../../../../../helpers";
import { useNavigate } from "react-router-dom";
import { useDispatchWrapper } from "../../../../../../hooks";
import { downLoadFile, previewFile } from "../../../../../../features/FileUpload/utils";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../../../../../redux/messages/messagesActions";

interface IPolicyQuotesRightPanelProps {
  open: boolean;
  customerPolicyId: number | string;
  onClose: () => void;
}

export const PolicyQuotesRightPanel: React.FC<
  IPolicyQuotesRightPanelProps
> = (props) => {
  const { open, customerPolicyId, onClose } = props;
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();

  const [list, setList] = React.useState<IQuotesRequest[]>([]);
  const [loading, setLoading] = React.useState<LoadState>(LoadState.NotLoaded);

  const fetchList = React.useCallback(async () => {
    try {
      setLoading(LoadState.InProgress);
      const advanceFilter = [
        {
          column: ["customer_policy_id"],
          operator: "EQUAL",
          value: customerPolicyId,
          logicalOperator: "AND",
        },
      ];
      const res = await api.get(
        `${endPoints.quotes.fetchQuotes}?advanceFilter=${JSON.stringify(
          advanceFilter
        )}`
      );
      const data: IQuotesRequest[] = res.data.data || [];
      setList(data);
      setLoading(LoadState.Loaded);
    } catch (err) {
      setList([]);
      setLoading(LoadState.Failed);
    }
  }, [customerPolicyId]);

  React.useEffect(() => {
    if (open && customerPolicyId) {
      fetchList();
    }
  }, [open, customerPolicyId, fetchList]);

  const quoteTableProps: IDataTableV2Props = {
    isPagination: false,
    rowsPerPageOptions: 25,
    isDataLoading: loading === LoadState.InProgress,
    uniqueRowKeyName: "quote_id",
    tableCommandBarProps: {
      preDefinedPlugins: {
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [],
      },
      rightItems: {
        customPlugins: [],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        disableResize: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        isFirstColumnSticky: true,
        onRowCellRender: (value, row: IQuotesRequest) => {
          const attachment =
            Array.isArray(row.attachment) && row.attachment.length > 0
              ? row.attachment[0]
              : null;
          return (
            <StandardTableActions
              onEditClick={() => {
                navigate("/view-quotes/manage/" + row.quote_id);
              }}
              onDownloadPreview={
                row.attachment
                  ? async () => {
                      try {
                        dispatch(saveLoaderProgress());
                        await previewFile(attachment);
                      } catch (err) {
                        console.log(err);
                      } finally {
                        dispatch(saveLoaderCompleted());
                      }
                    }
                  : undefined
              }
              onDownLoadClick={
                row.attachment
                  ? async () => {
                      try {
                        dispatch(saveLoaderProgress());
                        await downLoadFile(attachment);
                      } catch (err) {
                        console.log(err);
                      } finally {
                        dispatch(saveLoaderCompleted());
                      }
                    }
                  : undefined
              }
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IQuotesRequest) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "quote_reference_number",
        headerName: "Quote/Reference#",
        fieldName: "quote_reference_number",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "insurer",
        headerName: "Insurer",
        fieldName: "insurer",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "type",
        headerName: "Policy Type",
        fieldName: "type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IQuotesRequest) => {
          return formatText(row.type || "");
        },
      },
      {
        key: "name",
        headerName: "Name",
        fieldName: "name",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IQuotesRequest) => {
          return (
            <CustomLink
              to={"/view-quotes/manage/" + row.quote_id}
              label={row.name}
            />
          );
        },
      },
      {
        key: "effective_date",
        headerName: "Effective Date",
        fieldName: "effective_date",
        renderType: DataTableV2RowRenderType.DATE,
      },
      {
        key: "insert_ts",
        headerName: "Date Created",
        fieldName: "insert_ts",
        renderType: DataTableV2RowRenderType.DATE_TIME,
      },
    ],
    rows: list,
  };

  return (
    <RightPanel open={open} heading="Quotes" width="70%" onClose={onClose}>
      <DataTableV2 {...quoteTableProps} />
    </RightPanel>
  );
};
